import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOrganisation } from '../contexts/OrganisationContext';
import apiService from '../services/api';
import { Project, Environment, Deployment } from '../types/api';
import toast from 'react-hot-toast';
import Logger from '../utils/logger';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import QuotaUsageWidget from '../components/Organisation/QuotaUsageWidget';
import { Button } from '../components/ui';

const DashboardPage: React.FC = () => {
  const { t } = (useTranslation as any)('dashboard');
  const { user } = useAuth();
  const { currentOrganisation, quotaUsage } = useOrganisation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const projectsData = await apiService.getProjects();
      setProjects(projectsData);

      if (projectsData.length > 0) {
        const allEnvironments: Environment[] = [];
        const allDeployments: Deployment[] = [];

        for (const project of projectsData) {
          try {
            const envs = await apiService.getProjectEnvironments(project.id);
            allEnvironments.push(...envs);

            for (const env of envs) {
              try {
                const deps = await apiService.getEnvironmentDeployments(env.id);
                allDeployments.push(...deps);
              } catch (error) {
                Logger.error(`Failed to load deployments for environment`);
              }
            }
          } catch (error) {
            Logger.error('Failed to load environments for project');
          }
        }

        setEnvironments(allEnvironments);
        setDeployments(allDeployments);
      }
    } catch (error) {
      Logger.error('Failed to load dashboard data');
      toast.error(t('loadingError'));
    } finally {
      setLoading(false);
    }
  };

  if (!currentOrganisation && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
        <h3 className="mt-2 text-sm font-medium text-text-primary">{t('noOrg.title')}</h3>
        <p className="mt-1 text-sm text-text-tertiary">
          {t('noOrg.description')}
        </p>
        <div className="mt-6">
          <Link to="/organisations">
            <Button variant="primary" size="md">
              {t('noOrg.viewOrganisations')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" aria-hidden="true"></div>
        <span className="sr-only">{t('loadingDashboard')}</span>
      </div>
    );
  }

  return (
    <>
      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">{t('title')}</h1>
        <p className="mt-1 text-text-secondary text-sm">
          {t('welcome', { name: user?.prenom })}
        </p>
      </div>

      {/* Organization Context Card */}
      {currentOrganisation && (
        <section className="mb-8">
          <div className="bg-surface-2 rounded-card shadow-card border-2 border-blue-500 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-500/12 rounded-lg">
                  <BuildingOfficeIcon className="h-8 w-8 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-primary">{currentOrganisation.nom}</h2>
                  <p className="text-text-tertiary mt-1 text-sm">
                    {t('orgCard.plan')} <span className="font-semibold text-accent-400">{currentOrganisation.plan_type}</span>
                  </p>
                </div>
              </div>
              <Link to={`/organisations/${currentOrganisation.id}/dashboard`}>
                <Button variant="primary" size="md">
                  {t('orgCard.orgDashboard')}
                </Button>
              </Link>
            </div>

            {quotaUsage && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-surface-3 rounded-lg p-4 border border-border-1">
                  <QuotaUsageWidget quotaType="cpu" usage={quotaUsage.cpu} showDetails={true} />
                </div>
                <div className="bg-surface-3 rounded-lg p-4 border border-border-1">
                  <QuotaUsageWidget quotaType="ram" usage={quotaUsage.ram} showDetails={true} />
                </div>
                <div className="bg-surface-3 rounded-lg p-4 border border-border-1">
                  <QuotaUsageWidget quotaType="storage" usage={quotaUsage.storage} showDetails={true} />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick Actions Section */}
      <section className="mb-12">
        <h2 className="text-xl font-bold text-text-primary mb-6">{t('quickActions.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="h-1 bg-accent-500 mb-4 rounded"></div>
            <h3 className="text-base font-bold text-text-primary mb-2">{t('quickActions.projects')}</h3>
            <p className="text-text-secondary text-sm mb-4">{t('quickActions.projectsDescription')}</p>
            <Link to="/projects">
              <Button variant="primary" size="md">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                {t('quickActions.manage')}
              </Button>
            </Link>
          </div>

          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="h-1 bg-green-500 mb-4 rounded"></div>
            <h3 className="text-base font-bold text-text-primary mb-2">{t('quickActions.status')}</h3>
            <p className="text-text-secondary text-sm mb-4">{t('quickActions.statusDescription')}</p>
            <Link to="/status">
              <Button variant="success" size="md">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
                {t('quickActions.viewStatus')}
              </Button>
            </Link>
          </div>

          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="h-1 bg-purple-500 mb-4 rounded"></div>
            <h3 className="text-base font-bold text-text-primary mb-2">{t('quickActions.new')}</h3>
            <p className="text-text-secondary text-sm mb-4">{t('quickActions.newDescription')}</p>
            <Link to="/projects">
              <Button variant="primary" size="md" className="bg-purple-600 hover:bg-purple-700">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                </svg>
                {t('quickActions.start')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Statistics */}
      <section className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-text-primary">{t('overview.title')}</h2>
          <Link to="/status">
            <Button variant="primary" size="md">
              {t('overview.fullDashboard')}
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500/12 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h6a1 1 0 011 1v2a1 1 0 01-1 1h-6a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h6a1 1 0 011 1v2a1 1 0 01-1 1h-6a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-text-tertiary">{t('overview.projects')}</p>
                <p className="text-2xl font-bold text-text-primary">{projects.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500/12 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-text-tertiary">{t('overview.environments')}</p>
                <p className="text-2xl font-bold text-text-primary">{environments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500/12 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H4.414l2.293 2.293a1 1 0 01-1.414 1.414L3 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V5.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 4H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 14H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 12H14a1 1 0 010-2h4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-text-tertiary">{t('overview.applications')}</p>
                <p className="text-2xl font-bold text-text-primary">{deployments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500/12 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-text-tertiary">{t('overview.active')}</p>
                <p className="text-2xl font-bold text-text-primary">{deployments.filter(d => d.statut_deploiement === 'DEPLOYED' || d.statut_deploiement === 'RUNNING' || d.statut_deploiement === 'HEALTHY').length}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DashboardPage;
